import { z } from 'zod';

import { DefaultHeaders } from '@@api/headers';
import { defineService } from '@@api/lib/defineService';
import {
    Category,
    CollectionItem,
    Locale,
    Menu,
    NewCategory,
    PageStyle,
    Sitemap,
    Tenant,
    TenantGroup,
    UnityCategory,
} from '@@api/services/tenant/schemas';
import {
    deserializeCategory,
    deserializeMenu,
    deserializeSitemap,
    serializeCategory,
    serializeMenu,
    serializeSitemap,
} from '@@api/services/tenant/transformers';
import { PaginationParams, SearchParams } from '@@api/utils/schemas/queryParams';
import { ContentLocale, Ids } from '@@api/utils/schemas/schemas';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

export const tenantService = defineService(
    (defineEndpoint) => ({
        category: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/categories',
                query: z.object({
                    ...SearchParams.pick({ q: true, tenantIds: true }).shape,
                    ...PaginationParams.pick({ sort: true, size: true }).shape,
                    active: z.boolean().nullish(),
                    syncedWithDesknet: z.boolean().optional(),
                    name: z.string().optional(),
                    includeIds: Ids.optional(),
                    contentLocale: ContentLocale.optional(),
                }),
                headers: DefaultHeaders.pick({ 'x-unity-content-language': true }),
                responses: {
                    200: z.array(UnityCategory.transform(deserializeCategory)),
                },
            }),
            getAllPaginated: defineEndpoint({
                method: 'GET',
                path: '/categories',
                query: z.object({
                    ...SearchParams.pick({ q: true, tenantIds: true }).shape,
                    ...PaginationParams.shape,
                    active: z.boolean().nullish(),
                    syncedWithDesknet: z.boolean().optional(),
                    name: z.string().optional(),
                    includeIds: Ids.optional(),
                    contentLocale: ContentLocale.optional(),
                }),
                responses: {
                    200: paginated(UnityCategory.transform(deserializeCategory)),
                },
            }),
            head: defineEndpoint({
                method: 'HEAD',
                path: '/categories',
                query: z.object({
                    name: z.string(),
                    excludeId: z.number(),
                }),
                responses: {
                    200: z.never(),
                    404: z.never(),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/categories/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: UnityCategory.transform(deserializeCategory),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/categories/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Category.transform(serializeCategory),
                responses: {
                    200: UnityCategory.transform(deserializeCategory),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/categories',
                body: NewCategory.transform(serializeCategory),
                responses: {
                    201: UnityCategory.transform(deserializeCategory),
                },
            }),
        },
        tenantGroup: {
            get: defineEndpoint({
                method: 'GET',
                path: '/tenant-groups',
                query: z.object({
                    contentLocale: ContentLocale.nullish(),
                }),
                responses: {
                    200: z.array(TenantGroup),
                },
            }),
        },
        tenant: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/tenants',
                query: z.object({
                    ...SearchParams.pick({ tenantIds: true }).shape,
                    contentLocale: ContentLocale.nullish(),
                }),
                responses: {
                    200: z.array(Tenant),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/tenants/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: Tenant,
                },
            }),
        },
        collection: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/collections/brixwireKeyword/items',
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                    empty: z.boolean().optional(),
                }),
                responses: {
                    200: z.array(CollectionItem),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/collections/brixwireKeyword/items/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: CollectionItem,
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/collections/brixwireKeyword/items/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: CollectionItem,
                responses: {
                    200: CollectionItem,
                },
            }),
        },
        menu: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/menus',
                query: z.object({
                    tenantId: z.number(),
                }),
                responses: {
                    200: z.array(Menu.transform(deserializeMenu)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/menus/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: Menu.transform(deserializeMenu),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/menus/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Menu.transform(serializeMenu),
                responses: {
                    200: Menu.transform(deserializeMenu),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/menus',
                body: Menu.transform(serializeMenu),
                responses: {
                    201: Menu.transform(deserializeMenu),
                },
            }),
        },
        sitemaps: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/tenants/:id/sitemaps',
                pathParams: z.object({
                    id: z.number(),
                }),
                query: z.object({
                    ...SearchParams.pick({ q: true }).shape,
                }),
                responses: {
                    200: z.array(Sitemap.transform(deserializeSitemap)),
                },
            }),
            head: defineEndpoint({
                method: 'HEAD',
                path: '/tenants/:id/sitemaps',
                pathParams: z.object({
                    id: z.number(),
                }),
                query: z.object({
                    urlSlug: z.string(),
                    parentId: z.number().nullish(),
                    excludeId: z.number(),
                }),
                responses: {
                    200: z.never(),
                    404: z.never(),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/tenants/:tenantId/sitemaps/:sitemapId',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    tenantId: z.number(),
                    sitemapId: z.number(),
                }),
                responses: {
                    200: Sitemap.transform(deserializeSitemap),
                },
            }),
            putAll: defineEndpoint({
                method: 'PUT',
                path: '/tenants/:tenantId/sitemaps',
                pathParams: z.object({
                    tenantId: z.number(),
                }),
                body: z.array(z.any()),
                responses: {
                    200: z.array(Sitemap.transform(deserializeSitemap)),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/tenants/:tenantId/sitemaps/:sitemapId',
                pathParams: z.object({
                    tenantId: z.number(),
                    sitemapId: z.number(),
                }),
                body: Sitemap.transform(serializeSitemap),
                responses: {
                    200: Sitemap.transform(deserializeSitemap),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/tenants/:id/sitemaps',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: Sitemap.transform(serializeSitemap),
                responses: {
                    201: Sitemap.transform(deserializeSitemap),
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/tenants/:tenantId/sitemaps/:sitemapId',
                pathParams: z.object({
                    tenantId: z.number(),
                    sitemapId: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        pageStyles: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/pageStyles',
                responses: {
                    200: z.array(PageStyle),
                },
            }),
        },
        locales: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/locales',
                responses: {
                    200: z.array(Locale),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.tenantUrl,
    },
);
